import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@mui/system';
import { FieldArray, useFormikContext } from 'formik';
import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import PaymentMethodSplitForm, {
  PaymentMethodSplitFormRef,
} from 'src/components/PaymentMethodSplitForm';
import { SectionHeader } from 'src/components/SectionHeader';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { SwipeCard } from 'src/components/SwipeCard';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { startUpdateCC } from 'src/models/user/user';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { FormLifecycleStageSelect } from 'src/shared/Forms/Selects/LifecycleStageSelect';

import { useNewClientPage } from '../useNewClientPage';

import { ProfileField } from './ProfileField';

const gridSize = {
  xs: 12,
  sm: 6,
  md: 4,
};

export const PersonalInfoForm: FC<{
  fromLead?: boolean;
  addMany: boolean;
  isRequired: (id: string) => boolean;
  setHasAnyCards: Dispatch<SetStateAction<boolean[]>>;
  setPaymentFormRefs: (
    index: number,
    node: PaymentMethodSplitFormRef | null
  ) => void;
}> = ({
  fromLead,
  addMany,
  setPaymentFormRefs,
  setHasAnyCards,
  isRequired,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'client.newClientPage',
  });
  const { t: tPersonalInfo } = useTranslation('translation', {
    keyPrefix: 'client.personalInfo',
  });

  const { session, user } = useSession();

  const navigate = useNavigate();

  const { values, setFieldValue } = useFormikContext<any>();

  const { lifecycleStages, getProfileFieldOptions } = useNewClientPage();

  const newClientHandler = async (values, setFieldValue) => {
    const clientStage = lifecycleStages.find((l) => l.default === true);
    const res = await startUpdateCC({
      forClient: true,
    });
    const profileFields = values.invites[0].profileFields.map((f) => {
      return {
        name: f.name,
        id: f.id,
        value: null,
      };
    });
    const obj = Object.keys(values.invites[0]).reduce(
      (o, k) => ((o[k] = ''), o),
      {}
    );
    setFieldValue('invites', [
      ...values.invites,
      {
        ...obj,
        profileFields,
        secret: res.clientSecret,
        lifecycleStageId: clientStage ? clientStage.id : null,
      },
    ]);
    if (values.invites.length > 1) {
      setFieldValue('packageData', null);
    }
  };

  return (
    <StickyCard title={tPersonalInfo('personalInfo')}>
      {addMany ? (
        <Stack spacing={2}>
          <FormTextField
            name="emails"
            label={t('enterMultipleEmailAddresses')}
            required
            placeholder={t('pleaseEnterOneOrMoreEmailAddresses')}
            multiline
            rows={4}
            fast
            onChange={(e) => {
              const v = e.target.value;
              setFieldValue('emails', v);
              navigate(`/clients/new?emails=${encodeURIComponent(v)}`, {
                replace: true,
              });
            }}
          />
          <Typography color="textSecondary" component="span">
            <Typography fontWeight="bold" component="span">
              {t('tip')}:{' '}
            </Typography>
            {t('tipDesc')}
          </Typography>
          <Typography color="textSecondary" component="span">
            <Typography fontWeight="bold" component="span">
              {t('note')}:{' '}
            </Typography>
            {t('noteDesc')}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <FieldArray
            name="invites"
            render={({ remove }) => (
              <>
                {values.invites.map((invite, index) => (
                  <Card key={index}>
                    {values.invites.length > 1 && (
                      <SectionHeader
                        title={`${t('account')} ${index + 1}`}
                        bottomDivider
                        titleAdornment={
                          index !== 0 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => remove(index)}
                            >
                              <Icon icon={faTrash} size="small" />
                            </IconButton>
                          )
                        }
                      />
                    )}
                    <Grid key={index} container spacing={2} sx={{ p: 2 }}>
                      <Grid size={gridSize}>
                        <FormTextField
                          name={`invites.${index}.email`}
                          label={tPersonalInfo('email')}
                          required
                          fast
                          placeholder={tPersonalInfo('enterEmail')}
                        />
                      </Grid>
                      <Grid size={gridSize}>
                        <FormTextField
                          name={`invites.${index}.firstName`}
                          label={tPersonalInfo('firstName')}
                          required={isRequired('first_name')}
                          fast
                          placeholder={tPersonalInfo('firstName')}
                        />
                      </Grid>
                      <Grid size={gridSize}>
                        <FormTextField
                          name={`invites.${index}.lastName`}
                          label={tPersonalInfo('lastName')}
                          required={isRequired('last_name')}
                          fast
                          placeholder={tPersonalInfo('lastName')}
                        />
                      </Grid>
                      <Grid size={gridSize}>
                        <FormTextField
                          name={`invites.${index}.streetAddress`}
                          label={tPersonalInfo('street')}
                          required={isRequired('street_address')}
                          fast
                          placeholder={tPersonalInfo('address')}
                        />
                      </Grid>
                      <Grid size={gridSize}>
                        <FormTextField
                          name={`invites.${index}.city`}
                          label={tPersonalInfo('city')}
                          required={isRequired('city')}
                          fast
                          placeholder={tPersonalInfo('city')}
                        />
                      </Grid>
                      <Grid size={gridSize} container spacing={2}>
                        <Grid size={6}>
                          <FormTextField
                            name={`invites.${index}.state`}
                            label={t('state')}
                            required={isRequired('state')}
                            fast
                            placeholder={t('state')}
                          />
                        </Grid>
                        <Grid size={6}>
                          <FormTextField
                            name={`invites.${index}.zipCode`}
                            label={tPersonalInfo('zipCode')}
                            required={isRequired('zip_code')}
                            fast
                            placeholder={tPersonalInfo('zipCode')}
                          />
                        </Grid>
                      </Grid>
                      <Grid size={gridSize}>
                        <FormLifecycleStageSelect
                          name={`invites.${index}.lifecycleStageId`}
                          label={t('lifecycleStage')}
                          placeholder={t('selectAStage')}
                        />
                      </Grid>
                      {values.invites[index].profileFields.map((field, i) => (
                        <Grid key={i} size={gridSize}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {field.name}{' '}
                            {field.id === 'height' &&
                              (user.usesMetric ? '(cm)' : '(in)')}
                            {field.id === 'weight' &&
                              (user.usesMetric ? '(kg)' : '(lbs)')}
                            {!(session.newClientRequiredFields || []).find(
                              (f) => f.id === field.id
                            ) ? (
                              <Link
                                component="button"
                                sx={{ ml: 0.5 }}
                                onClick={() => {
                                  values.invites.forEach((client, index) => {
                                    setFieldValue(
                                      `invites.${index}.profileFields`,
                                      client.profileFields.filter(
                                        (f) => f.id !== field.id
                                      )
                                    );
                                  });
                                }}
                              >
                                <Icon
                                  size="tiny"
                                  color="error"
                                  icon={faTrash}
                                />
                              </Link>
                            ) : (
                              ' *'
                            )}
                          </Typography>
                          <ProfileField
                            field={field}
                            name={`invites.${index}.profileFields.${i}.value`}
                          />
                        </Grid>
                      ))}
                      <Grid
                        size={12}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          pt: 2,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {t('paymentInformation')}
                        </Typography>
                        {!invite.swipeCard && (
                          <PaymentMethodSplitForm
                            ref={(el) => {
                              setPaymentFormRefs(index, el);
                              // paymentFormRefs.current[index] = el;
                            }}
                            clientSecret={invite.secret}
                            onCardNumberChange={(e) => {
                              setHasAnyCards((pre) => {
                                pre[index] = e.complete;
                                return [...pre];
                              });
                            }}
                          />
                        )}
                        <SwipeCard
                          model={{
                            swipeCard: invite.swipeCard,
                            paymentMethodId: invite.paymentMethodId,
                          }}
                          onModelUpdate={(key, value) => {
                            setFieldValue(`invites.${index}.${key}`, value);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                ))}

                {getProfileFieldOptions(values.invites[0].profileFields || [])
                  .length > 0 && (
                  <TextField
                    value={''}
                    options={[
                      { label: t('addProfileField'), value: '' },
                      ...getProfileFieldOptions(
                        values.invites[0].profileFields || []
                      ),
                    ]}
                    onChange={(e) => {
                      const options = getProfileFieldOptions(
                        values.invites[0].profileFields || []
                      );
                      const field = e.target.value;
                      const f = options.find((f) => f.value == field);
                      if (field) {
                        values.invites.forEach((client, index) => {
                          setFieldValue(`invites.${index}.profileFields`, [
                            ...client.profileFields,
                            {
                              name: f.label,
                              id: f.value,
                              value: null,
                            },
                          ]);
                        });
                      }
                    }}
                  />
                )}

                {!fromLead && (
                  <Button
                    color="primary"
                    variant="bordered"
                    onClick={() => newClientHandler(values, setFieldValue)}
                    startIcon={<Icon icon={faPlus} />}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {t('addAnotherClient')}
                  </Button>
                )}
              </>
            )}
          />
        </Stack>
      )}
    </StickyCard>
  );
};
